import { type ReactNode, Suspense, use, useEffect, useLayoutEffect, useRef, useState } from 'react'

interface LazyHydrateProps {
  children: ReactNode
  /** Hydrate when the element scrolls into (or near) the viewport */
  whenVisible?: boolean
  /** Hydrate on a specific DOM event (e.g. "click") */
  on?: string
  /** @deprecated No longer used. Kept for API compatibility. */
  as?: 'Fragment'
}

const CONTENTS_STYLE = { display: 'contents' as const }

/**
 * Minimal element for IntersectionObserver.
 * The wrapper uses display:contents (no bounding box), so we need a real
 * element for IO to observe. Using position:absolute removes it from normal
 * flow so it does not become a flex/grid item and does not contribute to
 * gap calculations. Without explicit top/left it sits at its static position,
 * which is near the content it represents — good enough for IO + rootMargin.
 */
const SENTINEL_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  pointerEvents: 'none',
  visibility: 'hidden',
}

// useLayoutEffect on the client for synchronous state updates after DOM commit;
// useEffect on the server to avoid React SSR warnings.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

interface HydrationControl {
  promise: Promise<void>
  resolve: () => void
  isResolved: boolean
}

function createHydrationControl(resolveImmediately: boolean, onResolve: () => void): HydrationControl {
  if (resolveImmediately) {
    return { promise: Promise.resolve(), resolve: () => {}, isResolved: true }
  }

  let resolvePromise!: () => void
  const control: HydrationControl = {
    promise: new Promise<void>((r) => {
      resolvePromise = () => {
        control.isResolved = true
        r()
        onResolve()
      }
    }),
    resolve: null!,
    isResolved: false,
  }
  control.resolve = resolvePromise
  return control
}

/**
 * Inner component that suspends until the hydration promise resolves.
 * Uses React 19's `use()` hook to suspend on the pending promise, which tells
 * React to defer this subtree. During SSR hydration, React's selective hydration
 * keeps the server-rendered HTML visible while this component is suspended
 * inside a Suspense boundary.
 *
 * The `isReady` prop (driven by React state in the parent) provides a reliable
 * re-render trigger via standard state updates, ensuring the component
 * unsuspends in all environments including test runners.
 */
function SuspendUntilReady({
  isReady,
  promise,
  children,
}: {
  isReady: boolean
  promise: Promise<void>
  children: ReactNode
}) {
  if (!isReady) {
    use(promise)
  }
  return <>{children}</>
}

/**
 * Defers hydration of server-rendered children until a trigger fires.
 *
 * Uses React's selective hydration via Suspense + the `use()` hook
 * to keep server-rendered HTML visible while deferring component hydration.
 * When the trigger fires (visibility or DOM event), the promise resolves
 * and React hydrates the children against the existing server HTML.
 *
 * The wrapper uses `display: contents` so it's invisible to layout (flex, grid, etc.).
 * A tiny sentinel element provides a bounding box for IntersectionObserver since
 * display:contents elements have no box of their own.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LazyHydrate({ children, whenVisible, on, as }: LazyHydrateProps) {
  const resolveImmediately = typeof window === 'undefined' || (!whenVisible && !on)
  const [isReady, setIsReady] = useState(resolveImmediately)

  const controlRef = useRef<HydrationControl | null>(null)
  if (!controlRef.current) {
    controlRef.current = createHydrationControl(resolveImmediately, () => setIsReady(true))
  }

  const sentinelRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Set up visibility-based hydration trigger.
  // Uses useLayoutEffect so that immediate-resolve cases (no SSR content,
  // no trigger, etc.) produce synchronous state updates, which ensures the
  // Suspense boundary unsuspends within a single act() pass in tests.
  useIsomorphicLayoutEffect(() => {
    const control = controlRef.current
    if (!control || control.isResolved) return

    if (!whenVisible) {
      control.resolve()
      return
    }

    const wrapper = wrapperRef.current
    if (!wrapper) {
      control.resolve()
      return
    }

    // If there is no SSR content to preserve (client-side navigation or tests),
    // hydrate immediately rather than waiting for a trigger that may never fire.
    // During SSR hydration the wrapper contains server-rendered children beyond
    // the sentinel; during client navigation only the sentinel is present.
    if (wrapper.childElementCount <= 1) {
      control.resolve()
      return
    }

    const el = sentinelRef.current
    if (!el) {
      control.resolve()
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          control.resolve()
          observer.disconnect()
        }
      },
      { rootMargin: '250px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [whenVisible, on])

  // Handle event-based hydration (e.g. on="click")
  useEffect(() => {
    const control = controlRef.current
    if (!on || !control || control.isResolved) return

    const el = wrapperRef.current
    if (!el) return

    const handler = () => control.resolve()
    el.addEventListener(on, handler)
    return () => el.removeEventListener(on, handler)
  }, [on])

  return (
    <div ref={wrapperRef} style={CONTENTS_STYLE}>
      <div ref={sentinelRef} style={SENTINEL_STYLE} aria-hidden="true" />
      <Suspense>
        <SuspendUntilReady isReady={isReady} promise={controlRef.current.promise}>
          {children}
        </SuspendUntilReady>
      </Suspense>
    </div>
  )
}
